import React, { useEffect, useRef, useState } from 'react';
import { IoIosAttach } from 'react-icons/io';
import { helpFeedbackAPI } from '../services/api';
import './HelpFeedbackModal.css';

const STRINGS = {
  en: {
    title: 'Need Help or Have Feedback?',
    guide_lead: 'New to PRISM? Check out our',
    guide_link: 'Getting Started User Guide (PDF)',
    guide_still: 'Still need help? Tell us what you need below.',
    guide_loading: 'Loading user guide…',
    guide_failed: 'User guide is temporarily unavailable.',
    email: 'Email Address',
    email_ph: 'name@example.com',
    category: 'Category',
    frequency: 'Frequency',
    cat_select: 'Select a category',
    cat_login: 'Log in issues',
    cat_tech: 'Technical / IT support',
    cat_clinical: 'Suggest clinical content',
    cat_features: 'Tool features / enhancements',
    freq_select: 'How often does this occur?',
    freq_multi: 'Multiple times a day',
    freq_few: 'A few times per week',
    freq_some: 'Every so often',
    freq_rare: 'Rarely',
    details: 'Additional context',
    details_ph: 'Please provide details about the issue and any relevant URLs.',
    attach: 'Attach image',
    cancel: 'Cancel',
    submit: 'Submit',
    submitting: 'Submitting…',
    success: 'Thanks — your feedback has been received.',
    err_required: 'Please fill in all required fields.',
    err_email: 'Please enter a valid email address.',
    err_image_type: 'Image must be a .jpg, .jpeg, or .png file.',
    err_image_size: 'Image must be 10 MB or smaller.',
    // Screenshot filename rejections, keyed by the server's error code.
    err_filename_too_long: 'Image file name is too long. Please rename the image and try again.',
    err_filename_charset: 'Image file name contains characters that are not accepted (for example # or %). Please rename the image and try again.',
    err_filename_invalid: 'Image file name is not valid. Please rename the image and try again.',
    err_filename_required: 'This image has no file name. Please rename it and try again.',
    close: 'Close',
  },
  fr: {
    title: 'Besoin d’aide ou un commentaire à partager?',
    guide_lead: 'Nouveau sur PRISM? Consultez notre',
    guide_link: 'Guide de démarrage (PDF)',
    guide_still: 'Besoin d’aide supplémentaire? Dites-nous ci-dessous.',
    guide_loading: 'Chargement du guide d’utilisation…',
    guide_failed: 'Le guide d’utilisation est temporairement indisponible.',
    email: 'Adresse courriel',
    email_ph: 'nom@exemple.com',
    category: 'Catégorie',
    frequency: 'Fréquence',
    cat_select: 'Choisir une catégorie',
    cat_login: 'Problèmes de connexion',
    cat_tech: 'Soutien technique / TI',
    cat_clinical: 'Suggérer du contenu clinique',
    cat_features: 'Fonctionnalités / améliorations',
    freq_select: 'À quelle fréquence cela se produit-il?',
    freq_multi: 'Plusieurs fois par jour',
    freq_few: 'Quelques fois par semaine',
    freq_some: 'De temps en temps',
    freq_rare: 'Rarement',
    details: 'Contexte supplémentaire',
    details_ph: 'Veuillez fournir des détails sur le problème et toute URL pertinente.',
    attach: 'Joindre une image',
    cancel: 'Annuler',
    submit: 'Envoyer',
    submitting: 'Envoi…',
    success: 'Merci — vos commentaires ont été reçus.',
    err_required: 'Veuillez remplir tous les champs requis.',
    err_email: 'Veuillez saisir une adresse courriel valide.',
    err_image_type: 'L’image doit être un fichier .jpg, .jpeg ou .png.',
    err_image_size: 'L’image doit être de 10 Mo ou moins.',
    err_filename_too_long: 'Le nom du fichier image est trop long. Veuillez renommer l’image et réessayer.',
    err_filename_charset: 'Le nom du fichier image contient des caractères non acceptés (par exemple # ou %). Veuillez renommer l’image et réessayer.',
    err_filename_invalid: 'Le nom du fichier image n’est pas valide. Veuillez renommer l’image et réessayer.',
    err_filename_required: 'Cette image n’a pas de nom de fichier. Veuillez la renommer et réessayer.',
    close: 'Fermer',
  },
};

const CATEGORIES = [
  { value: 'login_issues', labelKey: 'cat_login' },
  { value: 'technical_support', labelKey: 'cat_tech' },
  { value: 'suggest_clinical_content', labelKey: 'cat_clinical' },
  { value: 'tool_features_enhancements', labelKey: 'cat_features' },
];

const FREQUENCIES = [
  { value: 'multiple_times_per_day', labelKey: 'freq_multi' },
  { value: 'few_times_per_week', labelKey: 'freq_few' },
  { value: 'every_so_often', labelKey: 'freq_some' },
  { value: 'rarely', labelKey: 'freq_rare' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const ALLOWED_IMAGE_EXTS = ['.jpg', '.jpeg', '.png'];

export default function HelpFeedbackModal({
  open,
  onClose,
  defaultEmail = '',
  language = 'en',
  lockedEmail = '',
}) {
  const s = STRINGS[language] || STRINGS.en;
  const fileInputRef = useRef(null);
  const firstFieldRef = useRef(null);

  // When lockedEmail is set (in-app, signed-in user), it overrides defaultEmail
  // and the input is rendered read-only.
  const emailLocked = Boolean(lockedEmail);
  const initialEmail = lockedEmail || defaultEmail;

  const [email, setEmail] = useState(initialEmail);
  const [category, setCategory] = useState('');
  const [frequency, setFrequency] = useState('');
  const [detailsText, setDetailsText] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // PDF guide signed URL — fetched fresh each time the modal opens so the
  // 30-minute expiry never bites and the bucket stays private.
  const [guideUrl, setGuideUrl] = useState('');
  const [guideStatus, setGuideStatus] = useState('idle'); // idle | loading | ready | failed

  useEffect(() => {
    if (!open) return;
    setEmail(lockedEmail || defaultEmail || '');
    setCategory('');
    setFrequency('');
    setDetailsText('');
    setImageFile(null);
    setSubmitting(false);
    setSubmitted(false);
    setError('');
    setTimeout(() => { if (firstFieldRef.current) firstFieldRef.current.focus(); }, 0);

    let cancelled = false;
    setGuideStatus('loading');
    setGuideUrl('');
    (async () => {
      try {
        const url = await helpFeedbackAPI.getUserManualUrl(language);
        if (cancelled) return;
        if (url) {
          setGuideUrl(url);
          setGuideStatus('ready');
        } else {
          setGuideStatus('failed');
        }
      } catch {
        if (!cancelled) setGuideStatus('failed');
      }
    })();
    return () => { cancelled = true; };
  }, [open, defaultEmail, lockedEmail, language]);

  if (!open) return null;

  const handleFilePick = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setImageFile(null);
      return;
    }
    const dot = file.name.lastIndexOf('.');
    const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
    if (!ALLOWED_IMAGE_EXTS.includes(ext)) {
      setError(s.err_image_type);
      e.target.value = '';
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError(s.err_image_size);
      e.target.value = '';
      return;
    }
    setError('');
    setImageFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !category || !frequency || !detailsText.trim()) {
      setError(s.err_required);
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setError(s.err_email);
      return;
    }
    setSubmitting(true);
    try {
      await helpFeedbackAPI.submit({
        email: email.trim(),
        category,
        frequency,
        detailsText: detailsText.trim(),
        imageFile,
      });
      setSubmitted(true);
      setTimeout(() => { onClose && onClose(true); }, 1600);
    } catch (err) {
      // Prefer the localized text for a coded filename rejection; err.message
      // holds the server's English detail as the fallback.
      const localized = err?.code ? s['err_' + err.code] : null;
      setError(localized || err?.message || 'Submission failed.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="help-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={s.title}
      onClick={() => !submitting && onClose && onClose(false)}
    >
      <div className="help-modal" onClick={(e) => e.stopPropagation()}>
        <div className="help-modal-header">
          <h2>{s.title}</h2>
          <button
            type="button"
            className="help-modal-close"
            aria-label={s.close}
            onClick={() => !submitting && onClose && onClose(false)}
          >
            ✕
          </button>
        </div>

        {submitted ? (
          <div className="help-modal-body help-modal-success">
            <div className="help-success-check">✓</div>
            <p>{s.success}</p>
          </div>
        ) : (
          <form className="help-modal-body" onSubmit={handleSubmit} noValidate>
            <div className="help-guide-banner" role="note">
              <span className="help-guide-icon" aria-hidden="true">📖</span>
              <div className="help-guide-text">
                <div>
                  {s.guide_lead}{' '}
                  {guideStatus === 'ready' && guideUrl ? (
                    <a
                      href={guideUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="help-guide-link"
                    >
                      {s.guide_link}
                    </a>
                  ) : guideStatus === 'failed' ? (
                    <span className="help-guide-link help-guide-link--disabled">
                      {s.guide_link}
                    </span>
                  ) : (
                    <span className="help-guide-link help-guide-link--loading">
                      {s.guide_link}
                    </span>
                  )}
                </div>
                <div className="help-guide-sub">{s.guide_still}</div>
              </div>
            </div>

            <label className="help-field">
              <span className="help-label">{s.email}</span>
              <input
                ref={firstFieldRef}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={s.email_ph}
                maxLength={254}
                required
                readOnly={emailLocked}
                aria-readonly={emailLocked}
                tabIndex={emailLocked ? -1 : 0}
                style={emailLocked ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
              />
            </label>

            <div className="help-row">
              <label className="help-field">
                <span className="help-label">{s.category}</span>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  required
                >
                  <option value="" disabled>{s.cat_select}</option>
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{s[c.labelKey]}</option>
                  ))}
                </select>
              </label>
              <label className="help-field">
                <span className="help-label">{s.frequency}</span>
                <select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  required
                >
                  <option value="" disabled>{s.freq_select}</option>
                  {FREQUENCIES.map((f) => (
                    <option key={f.value} value={f.value}>{s[f.labelKey]}</option>
                  ))}
                </select>
              </label>
            </div>

            <label className="help-field">
              <span className="help-label">{s.details}</span>
              <textarea
                value={detailsText}
                onChange={(e) => setDetailsText(e.target.value)}
                placeholder={s.details_ph}
                maxLength={4000}
                rows={3}
                required
              />
            </label>

            {error && <div className="help-error">{error}</div>}

            <div className="help-actions">
              <button
                type="button"
                className="help-attach-btn"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                title={s.attach}
                aria-label={s.attach}
              >
                <IoIosAttach size={22} color="#4B2A6D" />
                {imageFile && <span className="help-attach-name">{imageFile.name}</span>}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                onChange={handleFilePick}
                style={{ display: 'none' }}
              />
              <div className="help-actions-right">
                <button
                  type="button"
                  className="help-btn help-btn-cancel"
                  onClick={() => !submitting && onClose && onClose(false)}
                  disabled={submitting}
                >
                  {s.cancel}
                </button>
                <button
                  type="submit"
                  className="help-btn help-btn-submit"
                  disabled={submitting}
                >
                  {submitting ? s.submitting : s.submit}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
